import type { ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import {
  formatMoney,
  formatQty,
  formatRate,
  sideOf,
  type Currency,
  type MoneyFormatOptions,
} from '../lib/format.js';

export interface MoneyProps extends MoneyFormatOptions {
  /** A decimal string straight from the API. Never a number. */
  value: string | null | undefined;
  /** Colour by direction. Debit is copper, credit is jade — never red/green. */
  colorBySign?: boolean;
  /** Append ' Dr' / ' Cr', as a ledger balance column does. */
  showSide?: boolean;
  className?: string;
}

/**
 * The only correct way to put an amount on screen.
 *
 * Tabular figures, right alignment and consistent rounding are what make a
 * column of numbers readable; getting any of the three wrong in one place makes
 * the whole grid look broken.
 */
export function Money({
  value,
  colorBySign = false,
  showSide = false,
  className,
  ...options
}: MoneyProps) {
  const isNegative = typeof value === 'string' && value.startsWith('-');
  const text = formatMoney(value, options);

  return (
    <span
      className={cn(
        'bb-num tabular-nums',
        colorBySign && (isNegative ? 'text-[var(--debit)]' : 'text-[var(--credit)]'),
        className,
      )}
      title={value ?? undefined}
    >
      {text}
      {showSide && value ? (
        <span className="pl-1 text-[0.85em] text-[var(--ink-3)]">{sideOf(value)}</span>
      ) : null}
    </span>
  );
}

/** Balance as a ledger prints it: magnitude plus side, never a minus sign. */
export function Balance({
  value,
  currency = 'PKR',
  className,
}: {
  value: string | null | undefined;
  currency?: Currency;
  className?: string;
}) {
  if (!value) return <span className={cn('bb-num text-[var(--ink-3)]', className)}>—</span>;
  const side = sideOf(value);
  const magnitude = value.startsWith('-') ? value.slice(1) : value;

  return (
    <span className={cn('bb-num tabular-nums whitespace-nowrap', className)}>
      {formatMoney(magnitude, { currency, parenthesizeNegative: false })}
      <span
        className={cn(
          'pl-1 text-[0.85em]',
          side === 'Dr' ? 'text-[var(--debit)]' : 'text-[var(--credit)]',
        )}
      >
        {side}
      </span>
    </span>
  );
}

export function Qty({
  value,
  uom,
  decimals = 3,
  className,
}: {
  value: string | null | undefined;
  uom?: string;
  decimals?: number;
  className?: string;
}) {
  return (
    <span className={cn('bb-num tabular-nums whitespace-nowrap', className)}>
      {formatQty(value, decimals)}
      {uom && <span className="pl-1 text-[0.85em] text-[var(--ink-3)]">{uom}</span>}
    </span>
  );
}

export function Rate({
  value,
  from = 'USD',
  to = 'PKR',
  className,
}: {
  value: string | null | undefined;
  from?: Currency;
  to?: Currency;
  className?: string;
}) {
  return (
    <span className={cn('bb-num text-[12px] whitespace-nowrap', className)}>
      {from} 1 = {to} {formatRate(value)}
    </span>
  );
}

/**
 * Dual-currency display: the base figure with its USD equivalent underneath.
 * Every statement in this system renders in PKR with a USD toggle, and this is
 * the component that honours it.
 */
export function DualMoney({
  base,
  fxRate,
  showUsd,
  className,
}: {
  base: string | null | undefined;
  fxRate: string;
  showUsd: boolean;
  className?: string;
}) {
  if (!base) return <span className={cn('bb-num text-[var(--ink-3)]', className)}>—</span>;

  const usd = (Number(base) / Number(fxRate)).toFixed(4);

  return (
    <span className={cn('inline-flex flex-col items-end leading-tight', className)}>
      <Money value={base} currency="PKR" />
      {showUsd && (
        <span className="bb-num text-[10.5px] text-[var(--ink-3)]">
          {formatMoney(usd, { currency: 'USD' })}
        </span>
      )}
    </span>
  );
}

/** Metric tile for the dashboard: big monospace figure over an uppercase label. */
export function StatTile({
  label,
  value,
  hint,
  delta,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  /** Signed percentage string, e.g. '+8.4' or '-2.1'. */
  delta?: string;
  className?: string;
}) {
  const deltaNegative = delta?.startsWith('-');

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="bb-num text-[20px] leading-none font-semibold tracking-tight text-[var(--ink)]">
        {value}
      </div>
      <div className="bb-eyebrow pt-[5px]">{label}</div>
      {(hint || delta) && (
        <div className="flex items-center gap-2 pt-1.5 text-[11.5px]">
          {delta && (
            <span
              className={cn(
                'bb-num font-medium',
                deltaNegative ? 'text-[var(--debit)]' : 'text-[var(--credit)]',
              )}
            >
              {deltaNegative ? '' : '+'}
              {delta}%
            </span>
          )}
          {hint && <span className="text-[var(--ink-3)]">{hint}</span>}
        </div>
      )}
    </div>
  );
}

/**
 * A paired bar chart, in CSS, with no chart library behind it.
 *
 * Each entry is `[primary, secondary]` and the two are drawn **side by side from
 * the same baseline, on one shared scale**. Three things about that sentence were
 * not true before and each of them made the chart say something it should not:
 *
 * - **The two series had different scales.** The secondary was drawn at
 *   `(value / max) * 40` percent — four tenths of the height the primary got for
 *   the same number. A caller labelling them "receipts" and "payments" beside two
 *   equal legend swatches was inviting a comparison the geometry then denied: a
 *   week that paid out exactly what it took in drew a tall bar and a short one.
 * - **They were stacked, not paired.** Primary sat on top of secondary in one
 *   column, which reads as one bar totalling both — and on a shared scale would
 *   overflow the box. Two quantities being compared belong next to each other.
 * - **A small value drew nothing at all.** `(40000 / 1000000) * 100` is four
 *   hundredths of a percent of 168 pixels, which is no pixels. `minHeight` on a
 *   non-zero value makes a small bar a small bar; a true zero still draws
 *   nothing, because nothing is what happened.
 *
 * An empty `series` renders nothing and returns `null` rather than an empty box —
 * the caller is the only one that knows what "no data" should say.
 */
export function MiniBars({
  series,
  labels,
  height = 64,
  className,
}: {
  /** Each entry is [primary, secondary]; both share one scale. */
  series: Array<[number, number]>;
  labels?: string[];
  height?: number;
  className?: string;
}) {
  if (series.length === 0) return null;

  // The floor of 1 is only a divide-by-zero guard for an all-zero series; with
  // every bar at zero the scale it produces is never seen.
  const max = Math.max(...series.flat(), Number.MIN_VALUE);

  const bar = (value: number, tone: string, delay: number, label: string) => (
    <div
      className={`min-w-0 flex-1 rounded-t-[2px] ${tone}`}
      title={label}
      style={{
        height: `${(Math.max(value, 0) / max) * 100}%`,
        // Visible where there is something to see, and nothing where there is
        // not — `>` rather than `>=` is the whole of that distinction.
        minHeight: value > 0 ? '2px' : undefined,
        animation: `bb-grow-bar 0.5s var(--ease) ${delay}s both`,
      }}
    />
  );

  return (
    <div className={cn('flex items-end gap-1.5', className)} style={{ height }}>
      {series.map(([primary, secondary], i) => (
        <div
          key={i}
          className="flex flex-1 items-end gap-px"
          title={labels?.[i]}
          style={{ height: '100%' }}
        >
          {bar(primary, 'bg-[var(--accent)]', i * 0.03, labels?.[i] ?? '')}
          {bar(secondary, 'bg-[var(--line)]', i * 0.03 + 0.1, labels?.[i] ?? '')}
        </div>
      ))}
    </div>
  );
}
